import React, { useEffect, useState } from "react";
import Layout from "../components/app/Layout";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Plus, X, MapPin, Trophy, Trash2 } from "lucide-react";
import MapPreview from "../components/app/MapPreview";
import LocationPicker from "../components/app/LocationPicker";

const blank = {
  title: "", opponent: "", location: "", location_lat: null, location_lng: null,
  start_at: "", team: "", is_home: true, score_home: null, score_away: null, notes: "",
};

export default function MatchesPage() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get("/matches");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const payload = {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        score_home: form.score_home === "" || form.score_home == null ? null : Number(form.score_home),
        score_away: form.score_away === "" || form.score_away == null ? null : Number(form.score_away),
      };
      await api.post("/matches", payload);
      setForm(blank);
      setShowForm(false);
      load();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    }
  };

  const removeM = async (id) => {
    if (!window.confirm("?")) return;
    await api.delete(`/matches/${id}`);
    load();
  };

  const dateFmt = (iso) =>
    new Date(iso).toLocaleString(lang === "bg" ? "bg-BG" : "en-US", { dateStyle: "medium", timeStyle: "short" });

  const now = Date.now();
  const upcoming = list.filter((m) => new Date(m.start_at).getTime() >= now);
  const past = list.filter((m) => new Date(m.start_at).getTime() < now);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-mono mb-1">// {t("matches")}</div>
            <h1 className="font-oswald uppercase text-3xl sm:text-4xl tracking-tight">{t("matches")}</h1>
          </div>
          {user?.role === "coach" && (
            <button onClick={() => setShowForm(true)} className="btn-primary px-5 py-2.5 flex items-center gap-2" data-testid="btn-add-match">
              <Plus className="w-4 h-4" /> {t("addMatch")}
            </button>
          )}
        </div>

        {list.length === 0 ? (
          <div className="card-sharp p-12 text-center text-white/30 font-mono text-xs tracking-widest" data-testid="empty-matches">
            {t("noMatches")}
          </div>
        ) : (
          <>
            {upcoming.length > 0 && (
              <section>
                <div className="label-mono mb-3">// {t("upcoming")}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {upcoming.map((m) => (
                    <MatchCard key={m.id} m={m} t={t} lang={lang} dateFmt={dateFmt} canDelete={user?.role === "coach"} onDelete={() => removeM(m.id)} />
                  ))}
                </div>
              </section>
            )}
            {past.length > 0 && (
              <section>
                <div className="label-mono mb-3">// {t("past")}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {past.map((m) => (
                    <MatchCard key={m.id} m={m} t={t} lang={lang} dateFmt={dateFmt} canDelete={user?.role === "coach"} onDelete={() => removeM(m.id)} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-0 md:p-6" onClick={() => setShowForm(false)}>
          <div className="bg-[#0A1628] border border-white/10 w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0A1628]">
              <h2 className="font-oswald uppercase text-2xl">{t("addMatch")}</h2>
              <button onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-3">
              <Field label={t("title")} v={form.title} on={(v) => setForm({ ...form, title: v })} required testId="m-title" />
              <Field label={t("opponent")} v={form.opponent} on={(v) => setForm({ ...form, opponent: v })} required testId="m-opp" />
              <Field label={t("startsAt")} type="datetime-local" v={form.start_at} on={(v) => setForm({ ...form, start_at: v })} required testId="m-start" />
              <Field label={t("location")} v={form.location} on={(v) => setForm({ ...form, location: v })} testId="m-loc" />
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setForm({ ...form, is_home: true })} className={`py-2 border font-oswald uppercase tracking-wider text-sm ${form.is_home ? "border-pitch bg-pitch text-white" : "border-white/10 text-white/60"}`}>{t("home")}</button>
                <button type="button" onClick={() => setForm({ ...form, is_home: false })} className={`py-2 border font-oswald uppercase tracking-wider text-sm ${!form.is_home ? "border-pitch bg-pitch text-white" : "border-white/10 text-white/60"}`}>{t("away")}</button>
              </div>
              <div>
                <div className="label-mono mb-2">// {t("pickLocation")}</div>
                <LocationPicker
                  lat={form.location_lat}
                  lng={form.location_lng}
                  onChange={({ lat, lng }) => setForm({ ...form, location_lat: lat, location_lng: lng })}
                />
              </div>
              {err && <div className="text-red-400 text-sm font-mono">{err}</div>}
              <div className="flex gap-3 pt-3 sticky bottom-0 bg-[#0A1628] border-t border-white/10">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 btn-secondary py-3">{t("cancel")}</button>
                <button type="submit" className="flex-1 btn-primary py-3" data-testid="save-match">{t("save")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

function MatchCard({ m, t, lang, dateFmt, canDelete, onDelete }) {
  const past = new Date(m.start_at).getTime() < Date.now();
  return (
    <div className="card-sharp interactive p-5" data-testid={`match-${m.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="label-mono mb-1 flex items-center gap-2">
            <Trophy className="w-3 h-3 text-pitch" />
            <span>{m.is_home ? t("home") : t("away")}</span>
          </div>
          <div className="font-oswald uppercase text-2xl tracking-tight">{m.title}</div>
          <div className="text-white/70 mt-1">{t("vs")} <span className="text-pitch">{m.opponent}</span></div>
        </div>
        {canDelete && (
          <button onClick={onDelete} className="text-red-400 hover:text-red-300" data-testid={`del-match-${m.id}`}><Trash2 className="w-4 h-4" /></button>
        )}
      </div>
      <div className="flex flex-wrap gap-2 text-xs text-white/50 mb-3">
        <span className="tag">{dateFmt(m.start_at)}</span>
        {m.location && <span className="tag tag-pitch"><MapPin className="w-2.5 h-2.5 inline mr-1" />{m.location}</span>}
      </div>
      {past && (m.score_home != null || m.score_away != null) && (
        <div className="border-t border-white/5 pt-3 text-center">
          <div className="label-mono mb-1">{t("score")}</div>
          <div className="font-oswald text-3xl">
            <span className={m.is_home ? "text-pitch" : "text-white"}>{m.score_home ?? "-"}</span>
            <span className="text-white/30 mx-2">:</span>
            <span className={!m.is_home ? "text-pitch" : "text-white"}>{m.score_away ?? "-"}</span>
          </div>
        </div>
      )}
      {m.location_lat && m.location_lng && (
        <div className="mt-3 space-y-2">
          <MapPreview lat={m.location_lat} lng={m.location_lng} height={130} />
          <a
            href={`https://www.openstreetmap.org/?mlat=${m.location_lat}&mlon=${m.location_lng}#map=15/${m.location_lat}/${m.location_lng}`}
            target="_blank" rel="noopener noreferrer"
            className="block text-center btn-secondary py-2 text-xs"
            data-testid={`open-maps-${m.id}`}
          >
            {t("openInMaps")} →
          </a>
        </div>
      )}
    </div>
  );
}

function Field({ label, v, on, type = "text", required, testId }) {
  return (
    <div>
      <label className="label-mono block mb-1.5">{label}</label>
      <input
        type={type}
        value={v ?? ""}
        onChange={(e) => on(e.target.value)}
        required={required}
        className="w-full bg-[#111D33] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none"
        data-testid={testId}
      />
    </div>
  );
}
